import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { createCustomErrorContext, ErrorToken } from 'src/app/core/interceptors/generic-error-handling-interceptors';
import { environment } from 'src/environments/environment';
import { EquipmentContent } from '../models';

@Injectable()
export class EquipmentService {
  
  constructor(private http: HttpClient) {
  }

  getEquipment(idPage: any, size: any, filters: any) {
    let params = new HttpParams()
      .append('page', idPage)
      .append('size', size);
    if (filters && filters.model) {
      params = params.append('model', filters.model);
    }
    if (filters && filters.alphaname) {
      params = params.append('pbx.alphaName', filters.alphaname);
    }
    if (filters && filters.pbxId) {
      params = params.append('pbx.id', filters.pbxId);
    }
    return this.http.get(
      `${environment.apiEndpoint}/api/v1/equipments`, { params }
    );
  }

  getEquipmentByMacAddress(value: string, errorToken?: ErrorToken[]) {
    return this.http.get(
      `${environment.apiEndpoint}/api/v1/equipments/${value}`,
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  getEquipmentByIpui(value: string, errorToken?: ErrorToken[]) {
    return this.http.get(
      `${environment.apiEndpoint}/api/v1/equipments/by-handset-ipui/${value}`,
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  createEquipment(macAddress: string, model: string, pbxId: number, userId: string, errorToken?: ErrorToken[]) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/equipments?pbxId=${pbxId}&userId=${userId}`,
      {
        mac_address: macAddress,
        model: model,
      },
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  deleteEquipment(macAddress: string) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/equipments/${macAddress}`
    );
  }

  allocateToPbx(pbxId: number, macAddress: string) {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/equipments/${macAddress}`, ''
    );
  }

  deallocateFromPbx(pbxId: number, macAddress: string) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/equipments/${macAddress}`
    );
  }

  assignToUser(pbxId: number, userId: number, macAddress: string) {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/users/${userId}/equipments/${macAddress}`, ''
    );
  }

  unassignFromUser(pbxId: number, userId: number, macAddress: string) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/users/${userId}/equipments/${macAddress}`
    );
  }

  associateHandsetToEquipment(macAddress: string, ipui: string, errorToken?: ErrorToken[]) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/equipments/${macAddress}/handsets/${ipui}`, 
      '',
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  associateHandsetToEquipmentAndUser(pbxId: number, userId: string, macAddress: string, ipui: string, errorToken?: ErrorToken[]) {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/users/${userId}/equipments/${macAddress}/handsets/${ipui}`,
      '',
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  deleteHandset(macAddress: string, ipui: string) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/equipments/${macAddress}/handsets/${ipui}`
    );
  }

  assignHandsetToUser(
    pbxId: number,
    userId: number,
    macAddress: string,
    ipui: string
  ) {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/users/${userId}/equipments/${macAddress}/handsets/${ipui}`, ''
    );
  }

  unassignHandsetFromUser(
    pbxId: number,
    userId: number,
    macAddress: string,
    ipui: string
  ) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/users/${userId}/equipments/${macAddress}/handsets/${ipui}`
    );
  }

  getProvisioningFile(pbxId: any, macAddress: string) {
    return this.http.get(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/equipments/${macAddress}/provisioning-file`,
      { responseType: 'text' });
  }

  getOverrideFragment(pbxId: any, macAddress: string) {
    return this.http.get(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/equipments/${macAddress}/override-fragment`,
      { responseType: 'text' }
    );
  }

  updateOverrideFragment(pbxId: any, macAddress: string, file: FormData) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/equipments/${macAddress}/override-fragment`,
      file
    );
  }

  setEquipmentBlacklist(equipment: EquipmentContent) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/equipments/${equipment.mac_address}/blacklisted`,
      ''
    );
  }

  setEquipmentBlf(equipment: EquipmentContent) {
    const data = new FormData();
    data.append('blf', `${!equipment.blf}`);
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/equipments/${equipment.mac_address}/blf`,
      data
    );
  }

  setPbxBlf(pbxId: number, value: boolean) {
    const data = new FormData();
    data.append('blf', `${value}`);
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/equipments/blf`,
      data
    );
  }

  resetEquipmentProvisioning(mac: string) {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/equipments/${mac}/provisioning-reset`,
      null
    );

  }

  getEquipmentRegistered(mac: string) {
    return this.http.get(`${environment.apiEndpoint}/api/v1/equipments/${mac}/registered`);
  }

  rebootEquipmentById(id: number, errorToken?: ErrorToken[]) {
    return this.http.post(`${environment.apiEndpoint}/api/v1/equipments/${id}/reboot`, null, {
      observe: 'response',
      context: createCustomErrorContext(errorToken)
    });
  }

  updateOutboundDid(mac: string, did: string) {
    return this.http.put(`${environment.apiEndpoint}/api/v1/equipments/${mac}/outbound-did`,
      {
        number: did
      });
  }

  updateHandsetOutboundDid(mac: string, ipui: string, did: string) {
    return this.http.put(`${environment.apiEndpoint}/api/v1/equipments/${mac}/handsets/${ipui}/outbound-did`,
      {
        number: did
      });
  }

  updateFirmwareUpdateFlag(mac: string, flag: boolean) {
    let httpParams = new HttpParams().append('enable', `${flag}`);
    return this.http.post(`${environment.apiEndpoint}/api/v1/equipments/${mac}/firmware-update`, null, {
      params: httpParams
    });
  }

  sendInstructionsEmail(macAddress: string, errorToken?: ErrorToken[]) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/equipments/${macAddress}/send-instructions`,
      '',
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }
}
